import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, ICONS, errMsg, timeAgo } from '../ext/extUi';
import LanTabs from './LanTabs';

// LAN environments: one per venue event. Each gives an install command the venue runs on its
// server machines; the agents connect outbound, so the venue needs no port forwarding.
export default function LanEnvironments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnded, setShowEnded] = useState(false);
  const [name, setName] = useState('');
  const [autoApprove, setAutoApprove] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/lan/events', { params: showEnded ? { all: 1 } : {} });
      setList(r.data.data || []);
      setError('');
    } catch (e) { setError(errMsg(e, t('lan.loadFailed', 'Failed to load'))); }
    finally { setLoading(false); }
  }, [showEnded, t]);

  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true); setError('');
    try {
      const r = await axios.post('/api/lan/events', { name: name.trim(), auto_approve: autoApprove });
      navigate(`/admin/servers/lan/${r.data.id}`);
    } catch (err) { setError(errMsg(err, t('lan.createFailed', 'Failed to create'))); }
    finally { setCreating(false); }
  };

  return (
    <div className="lan-page">
      <LanTabs />
      <div className="lan-head">
        <h1 className="lan-title">{t('lan.title', 'LAN Environments')}</h1>
        <p className="lan-lead">
          {t('lan.subtitle', 'Run matches on servers at a LAN venue without port forwarding. Create an event, give the venue its install command, and run it on every server machine. The agent connects out to this panel and carries RCON and logs.')}
        </p>
      </div>

      <section className="lan-block">
        <h2 className="lan-h2" style={{ marginBottom: '0.4rem' }}>{t('lan.newEvent', 'New LAN event')}</h2>
        <p className="lan-sub">{t('lan.newEventHint', 'One event per venue (e.g. "Dubai LAN"). Its install command works on any number of machines.')}</p>
        <form onSubmit={create} className="lan-create">
          <input className="lan-input" value={name} onChange={e => setName(e.target.value)} maxLength={100} placeholder={t('lan.eventNamePlaceholder', 'Event name')} aria-label={t('lan.eventNamePlaceholder', 'Event name')} />
          <label className="lan-check">
            <input type="checkbox" checked={autoApprove} onChange={e => setAutoApprove(e.target.checked)} />
            {t('lan.autoApprove', 'Approve new machines automatically')}
          </label>
          <button type="submit" className="lan-btn primary" disabled={creating || !name.trim()}>
            <Icon d={ICONS.plus} /> {creating ? t('common.loading', 'Loading...') : t('lan.create', 'Create')}
          </button>
        </form>
        {error && <div className="lan-error" style={{ marginTop: '0.6rem' }}>{error}</div>}
      </section>

      <section className="lan-block">
        <div className="lan-block-head">
          <h2 className="lan-h2">{t('lan.events', 'Events')}</h2>
          <span className="lan-spacer" />
          <label className="lan-check">
            <input type="checkbox" checked={showEnded} onChange={e => setShowEnded(e.target.checked)} />
            {t('lan.showEnded', 'Show ended events')}
          </label>
        </div>
        {loading ? <div className="lan-muted">{t('common.loading', 'Loading...')}</div>
          : list.length === 0 ? <div className="lan-muted">{t('lan.noEvents', 'No LAN events yet.')}</div>
            : list.map(ev => (
              <Link key={ev.id} to={`/admin/servers/lan/${ev.id}`} className="lan-event">
                <span className="lan-event-name">{ev.name}</span>
                {ev.status === 'ended' && <span className="lan-tag">{t('lan.ended', 'Ended')}</span>}
                {ev.agents_pending > 0 && <span className="lan-tag warn">{t('lan.pendingN', '{{n}} pending', { n: ev.agents_pending })}</span>}
                <span className="lan-spacer" />
                <span className="lan-meta">
                  <span className={ev.agents_online > 0 ? 'lan-tag ok' : undefined}>{t('lan.machinesOnline', '{{online}}/{{total}} machines online', { online: ev.agents_online, total: ev.agents })}</span>
                  <span>{t('lan.serversN', '{{n}} servers', { n: ev.servers })}</span>
                  <span>{timeAgo(ev.created_at)}</span>
                </span>
              </Link>
            ))}
      </section>
    </div>
  );
}
